import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import GarrisonTimelineList from "./components/garrison-timeline-list.jsx";
import GarrisonTimelineSlider from "./components/garrison-timeline-slider.jsx";
import {Card} from "../../../../../components/card/card.jsx";
import * as Tabs from "@radix-ui/react-tabs";
import {garrisonTimelineModes, ROUTE_LEGEND_ITEMS} from "../../utils/garrison-timeline-utils.js";
import Timeline from "./components/timeline.jsx";
import GarrisonTimelineMap from "./components/map/garrison-timeline-map.jsx";
import GarrisonTimelineControlPanel from "./components/garrison-timeline-control-panel.jsx";
import {useTimelineAnimation} from "../../hooks/useTimelineAnimation.js";
import LegendDropdown from "../../../../../components/legend-dropdown/legend-dropdown.jsx";
const tabButtonStyle = "relative pt-1 w-[140px] font-bold border border-[#E2E8F0] border-b-0 rounded-t bg-[#EEF0F3]";
const tabButtonStyleActive = "text-[#063a6c] bg-white"

const HideBorderDiv = () => <div className="bg-white h-2 w-full bottom-0 translate-y-1 z-10 absolute"></div>;
const TimelineView = ({data, filter, mode, setMode, corporation, zoom, setZoom, elapsedSeconds, setElapsedSeconds, onVisualStateChange, hasNextPage, isFetchingNextPage, onLoadMore}) => {
  const [internalZoomRange, setInternalZoomRange] = useState({start: 0, end: 100});
  const timelineScrollRef = useRef(null);
  const stableFilter = useMemo(() => ({
    dtStart: filter?.dtStart,
    dtEnd: filter?.dtEnd
  }), [filter?.dtStart, filter?.dtEnd]);
  const zoomRange = zoom ?? internalZoomRange;
  const isTimeline = mode === garrisonTimelineModes.timeline;
  const isMap = mode === garrisonTimelineModes.map;
  const animationControl = useTimelineAnimation(elapsedSeconds, setElapsedSeconds, isMap ? 60 : 1);
  const handleModeChange = (nextMode) => {
    if (nextMode === garrisonTimelineModes.timeline) {
      animationControl.pause();
    }

    setMode(nextMode);
  };
  const handleZoomChange = useCallback((nextZoom) => {
    const updateZoom = setZoom ?? setInternalZoomRange;

    updateZoom(currentZoom => {
      const resolvedZoom = typeof nextZoom === "function" ? nextZoom(currentZoom) : nextZoom;

      return currentZoom.start === resolvedZoom.start && currentZoom.end === resolvedZoom.end ? currentZoom : resolvedZoom;
    });
  }, [setZoom]);
  const handleSegmentClick = useCallback((seconds) => {
    setElapsedSeconds(currentSeconds => currentSeconds === seconds ? currentSeconds : seconds);
  }, [setElapsedSeconds]);
  const handleTimelineWheel = useCallback((event) => {
    if (!isTimeline || event.ctrlKey || event.metaKey) return;

    timelineScrollRef.current?.scrollBy({top: event.deltaY, behavior: "auto"});
  }, [isTimeline]);

  useEffect(() => {
    onVisualStateChange?.({cropRange: animationControl.isCropMode ? animationControl.cropRange : null});
  }, [animationControl.cropRange, animationControl.isCropMode, onVisualStateChange]);

  const listData = useMemo(() => {
    if (!data?.garrisons) return data;

    return {
      ...data,
      garrisons: data.garrisons.map((garrison, index) => ({
        ...garrison,
        originalIndex: data.page * data.pageSize + index
      }))
    };
  }, [data]);
  return (
    <div className="w-full h-full flex flex-col gap-2 min-h-0 overflow-hidden relative">
      <Card className="flex-1 min-h-0">
        <Card.Body className="w-full h-full flex min-h-0 overflow-hidden">
          <div className="absolute top-3 right-5 z-[70] h-6 w-6">
            <LegendDropdown items={ROUTE_LEGEND_ITEMS} hint="Pressione CTRL + Scroll sobre a linha do tempo para aplicar zoom." />
          </div>
          <Tabs.Root className="flex h-full w-full min-h-0 flex-col" value={mode}>
            <div className="relative flex h-[2.1rem] shrink-0">
              <div className="absolute inset-x-0 bottom-0 z-0 border-b border-[#E2E8F0]" />
              <div className="flex-1 min-w-[260px] px-2 flex items-center relative z-[60] bg-white border-b border-[#E2E8F0]">
                <span className="text-[1.0625rem]">Guarnições</span>
              </div>
              <Tabs.List className="flex-5 ml-3 flex gap-1 pt-2 relative z-[60]">
                <Tabs.Trigger className={`${tabButtonStyle} ${isTimeline ? tabButtonStyleActive : ""}`} value={garrisonTimelineModes.timeline} onClick={() => handleModeChange(garrisonTimelineModes.timeline)}>
                  <span>LINHA DO TEMPO</span>
                  {isTimeline && <HideBorderDiv/>}
                </Tabs.Trigger>
                <Tabs.Trigger className={`${tabButtonStyle} ${isMap ? tabButtonStyleActive : ""}`} value={garrisonTimelineModes.map} onClick={() => handleModeChange(garrisonTimelineModes.map)}>
                  <span>MAPA</span>
                  {isMap && <HideBorderDiv/>}
                </Tabs.Trigger>
              </Tabs.List>
            </div>
            <div ref={timelineScrollRef} className={`flex min-h-0 flex-1 ${isTimeline ? "overflow-y-auto" : ""}`}>
              <div className={`flex-1 min-w-[260px] relative z-[60] bg-white ${isTimeline ? "h-auto self-start" : "min-h-0 h-full"}`}>
                <GarrisonTimelineList data={listData} filter={stableFilter} isMap={isMap} hasNextPage={hasNextPage} isFetchingNextPage={isFetchingNextPage} onLoadMore={onLoadMore} showHeader={false} />
              </div>
              <div className={`flex-5 ${isTimeline ? "self-start" : "min-h-0 h-full"}`} onWheelCapture={handleTimelineWheel}>
                <Tabs.Content className="" value={garrisonTimelineModes.timeline}>
                  <div id="timeline-top-anchor" />
                  <Timeline data={data} zoom={zoomRange} onZoomChange={handleZoomChange} onSegmentClick={handleSegmentClick} filter={stableFilter} hideTopBorder />
                </Tabs.Content>
                <Tabs.Content className="h-full w-full" value={garrisonTimelineModes.map}>
                  <div className="h-full w-full border border-t-0 border-l-0 border-[#E2E8F0]">
                    <GarrisonTimelineMap corporation={corporation} garrisonsList={listData?.garrisons} timelineData={data?.timeline} dispatches={data?.dispatches} elapsedSeconds={elapsedSeconds} filter={stableFilter} />
                  </div>
                </Tabs.Content>
              </div>
            </div>
          </Tabs.Root>
        </Card.Body>
      </Card>
      <div className="h-[100px] flex gap-2 print:hidden">
        <Card className="h-full flex-1 min-w-[260px] min-h-0 relative z-[60] bg-white">
          <Card.Body className="h-full w-full">
            <GarrisonTimelineControlPanel
              mode={mode}
              isPlaying={animationControl.isPlaying}
              onTogglePlay={animationControl.togglePlay}
              speedIndex={animationControl.speedIndex}
              setSpeedIndex={animationControl.setSpeedIndex}
              stepSeconds={animationControl.stepSeconds}
              setStepSeconds={animationControl.setStepSeconds}
              onStepForward={animationControl.stepForward}
              onStepBackward={animationControl.stepBackward}
              onRestart={animationControl.restart}
              isCropMode={animationControl.isCropMode}
              onToggleCrop={animationControl.toggleCropMode}
            />
          </Card.Body>
        </Card>
        <Card className="h-full flex-5">
          <GarrisonTimelineSlider
            filter={stableFilter}
            zoom={zoomRange}
            setZoom={handleZoomChange}
            isTimeline={isTimeline}
            elapsedSeconds={elapsedSeconds}
            setElapsedSeconds={setElapsedSeconds}
            isCropMode={animationControl.isCropMode}
            cropRange={animationControl.cropRange}
            setCropRange={animationControl.setCropRange}
          />
        </Card>
      </div>
    </div>
  );
};

export default TimelineView;
